<template>
  <Teleport to="body">
    <div class="shortcon_page" @click="close">
      <div class="shortcon_box" @click.stop="">
        <div class="header">
          <div class="title">{{ $t('shortcut_key_settings') }}</div>
          <div class="mgx_close_btn" @click="close">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="4" y1="4" x2="14" y2="14"></line>
              <line x1="4" y1="14" x2="14" y2="4"></line>
            </svg>
          </div>
        </div>

        <div class="combo_wrapper">
          <action_combo class="action_combo" :is-config="true" :keys="keyArray" />
        </div>

        <div class="keys_box scrollbar_thumb">
          <div class="section_title">{{ $t('modifier_keys') }}</div>
          <div class="keys_grid">
            <div class="key" v-for="(key, index) in ShortconKeys" :key="'short-' + key" :class="{ selected: findKey(key) }" @click.stop="setKey(Number(key))">
              {{ getStringAfterDash(kbVKTexture[key]) }}
            </div>
          </div>

          <div class="key_line"></div>

          <div class="section_title">{{ $t('base_keys') }}</div>
          <div class="keys_grid">
            <div class="key" v-for="(key, index) in BaseKeys()" :key="'base-' + key" :class="{ selected: keyArray[2] == key }" @click.stop="selectKey(Number(key))">
              {{ kbVKTexture[key] }}
            </div>
          </div>
        </div>

        <div class="button_box">
          <div class="mgx_action_btn primary" @click.stop="save" :class="{ disable: saving || !canSave }">
            <svg class="icon-wrapper" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
              <polyline points="17 21 17 13 7 13 7 21"></polyline>
              <polyline points="7 3 7 8 15 8"></polyline>
            </svg>
            <span>{{ $t('save') }}</span>
          </div>
          <div class="mgx_action_btn" @click.stop="cancelBind" :class="{ disable: saving || !isCurrentlyBound }">
            <svg class="icon-wrapper" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
            <span>{{ $t('clear_bind') }}</span>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watchEffect, computed, Teleport } from 'vue'
import { ShortconKeys, BaseKeys, kbVKTexture } from '../../define/vkcode'
import i18n from '@/languages/i18n'
let { t } = i18n.global
import action_combo from '../operation_action/action_combo.vue'
import { onMounted } from 'vue'
import { VK } from '@/define/define'
import { getStringAfterDash, normalizeKeyCode } from '@/tool/utility'
import { configSet } from '@/service/driver/config_set'
import { DialogType, enableDialog } from '../cdialog/dialog'
import { BaseTabs } from '@/service/driver/action_set'
const props = defineProps<{ index: number; keys: VK[] }>()
const keyArray = ref<VK[]>([0, 0, 0])

const hasModifier = computed(() => keyArray.value[0] > 0 || keyArray.value[1] > 0)
const hasBaseKey = computed(() => keyArray.value[2] > 0)
const canSave = computed(() => hasModifier.value && hasBaseKey.value)
const isCurrentlyBound = computed(() => props.keys && props.keys.some((k) => k > 0))

const watchData = () => {
  watchEffect(() => {
    keyArray.value = normalizeKeyCode(props.keys || [0, 0, 0], BaseTabs.compose, ShortconKeys)
  })
}
onMounted(() => {
  watchData()
})
const findKey = (key: number): boolean => {
  return keyArray.value.includes(key)
}
const setKey = (key: number) => {
  if (findKey(key)) {
    const idx = keyArray.value.indexOf(key)
    if (idx < 2) keyArray.value[idx] = 0
  } else {
    if (keyArray.value[0] === 0) {
      keyArray.value[0] = key
    } else if (keyArray.value[1] === 0) {
      keyArray.value[1] = key
    }
  }
}
const selectKey = (key: number) => {
  keyArray.value[2] = key
}
const saving = ref(false)
const save = () => {
  if (saving.value) return
  if (!canSave.value) {
    enableDialog(t('please_select_at_least_one_modifier_and_one_base_key'), DialogType.notice, false)
    return
  }
  saving.value = true
  configSet.apiBindShortcut(props.index, keyArray.value).then(
    () => {
      enableDialog(t('config_bind_success'), DialogType.notice, true)
      saving.value = false
    },
    () => {
      configSet.apiConfigList()
      saving.value = false
    },
  )
}
const cancelBind = () => {
  if (saving.value || !isCurrentlyBound.value) return
  saving.value = true
  configSet.apiBindShortcut(props.index, []).then(
    () => {
      enableDialog(t('config_cancel_bind_success'), DialogType.notice, true)
      saving.value = false
      keyArray.value = [0, 0, 0]
    },
    () => {
      configSet.apiConfigList()
      saving.value = false
    },
  )
}
const close = () => {
  configSet.isShowShortcon = false
}
</script>

<style scoped lang="less">
.shortcon_page {
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  z-index: 120;
  background: var(--glass-overlay-bg);
  backdrop-filter: var(--glass-overlay-blur);
  padding: 40px;

  .shortcon_box {
    width: 480px;
    height: 100%;
    padding: 30px;
    position: relative;
    background: var(--glass-panel-bg);
    backdrop-filter: var(--glass-panel-blur);
    border-radius: var(--radius-l);
    display: flex;
    flex-direction: column;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.1);
    animation: feedbackFadeIn 0.4s var(--transition-base);
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;

    .title {
      font-size: 20px;
      font-weight: 700;
      color: var(--color-dark);
    }
  }

  .combo_wrapper {
    background: rgba(255, 255, 255, 0.4);
    border-radius: var(--radius-m);
    padding: 24px;
    margin-bottom: 24px;

    .action_combo {
      box-shadow: none;
      margin: 0 !important;
    }
  }

  .keys_box {
    flex: 1;
    overflow-y: auto;
    padding-right: 8px;
    margin-bottom: 24px;
    text-align: center;

    .section_title {
      font-size: 13px;
      font-weight: 600;
      color: var(--color-text-hint);
      margin-bottom: 12px;
      margin-top: 16px;
    }

    .keys_grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, 46px); /* 自动填充，每列固定 46px */
      gap: 10px;
    }

    .key_line {
      width: 100%;
      height: 1px;
      background: rgba(0, 0, 0, 0.05);
      margin: 24px 0;
    }

    .key {
      aspect-ratio: 1/1;
      border-radius: var(--radius-s);
      background: rgba(255, 255, 255, 0.6);
      border: 1px solid rgba(0, 0, 0, 0.05);
      font-size: 14px;
      font-weight: 500;
      color: var(--color-dark);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      text-align: center;
      transition: var(--transition-base);

      &:hover {
        transform: translateY(-2px);
        background: #fff;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
        border-color: var(--brand-color-pure);
      }

      &.selected {
        background: var(--brand-color);
        border-color: transparent;
        color: #fff;
        box-shadow: var(--action-btn-primary-shadow);

        &:hover {
          transform: translateY(-2px);
          filter: brightness(1.1);
        }
      }
    }

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent !important;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.1);
      border-radius: var(--radius-pill);
    }
  }

  .button_box {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding-top: 20px;
    border-top: 1px solid rgba(0, 0, 0, 0.05);

    .mgx_action_btn {
      width: 120px;
      height: 38px;

      .icon-wrapper {
        width: 16px;
        height: 16px;
      }

      span {
        font-size: 13px;
      }

      &.disable {
        opacity: 0.5;
        cursor: not-allowed;
        filter: grayscale(1);
        pointer-events: none;
      }
    }
  }
}

@keyframes feedbackFadeIn {
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
